import { useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { ACCESS_REVIEWERS } from '../lib/access';
import type { AccessReviewer } from '../lib/access';

/**
 * Access-reviewer contact list (Settings → Access reviewers, root+maintainer).
 * Like Grafana contact points, layered: L1 = first responders auto-assigned to
 * every new request; higher layers are the manual-escalation groups. Deleting a
 * reviewer never touches open requests (assignees snapshot the email).
 */
const CREATE = gql`
  mutation CreateAccessReviewer($email: String!, $layer: Int!, $displayName: String) {
    createAccessReviewer(email: $email, layer: $layer, displayName: $displayName) {
      id
    }
  }
`;
const UPDATE = gql`
  mutation UpdateAccessReviewer($id: Int!, $layer: Int) {
    updateAccessReviewer(id: $id, layer: $layer) {
      id
      layer
    }
  }
`;
const REMOVE = gql`
  mutation DeleteAccessReviewer($id: Int!) {
    deleteAccessReviewer(id: $id)
  }
`;

export function AccessReviewers() {
  const { data, loading, error, refetch } = useQuery<{ accessReviewers: AccessReviewer[] }>(
    ACCESS_REVIEWERS,
    { fetchPolicy: 'cache-and-network' },
  );
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [layer, setLayer] = useState('1');
  const [mutErr, setMutErr] = useState<string | null>(null);

  const onDone = () => {
    setMutErr(null);
    void refetch();
  };
  const onErr = (e: Error) => setMutErr(e.message);
  const [create, { loading: creating }] = useMutation(CREATE, { onCompleted: onDone, onError: onErr });
  const [update, { loading: updating }] = useMutation(UPDATE, { onCompleted: onDone, onError: onErr });
  const [remove, { loading: removing }] = useMutation(REMOVE, { onCompleted: onDone, onError: onErr });

  const rows = data?.accessReviewers ?? [];
  const busy = creating || updating || removing;

  if (error) return <p className="error">{error.message}</p>;

  return (
    <div>
      {mutErr && <p className="error">{mutErr}</p>}

      <div className="filter-row" style={{ alignItems: 'center' }}>
        <input
          aria-label="Reviewer email"
          placeholder="reviewer@optisigns.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ minWidth: 260 }}
        />
        <input
          aria-label="Display name"
          placeholder="Display name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ minWidth: 140 }}
        />
        <select className="class-select" aria-label="Reviewer layer" value={layer} onChange={(e) => setLayer(e.target.value)}>
          {[1, 2, 3].map((l) => (
            <option key={l} value={l}>
              Layer {l}
            </option>
          ))}
        </select>
        <button
          className="btn btn-primary"
          disabled={busy || !email.trim().includes('@')}
          onClick={() => {
            void create({
              variables: { email: email.trim(), layer: Number(layer), displayName: name.trim() || null },
            });
            setEmail('');
            setName('');
          }}
        >
          Add reviewer
        </button>
      </div>

      {loading && !rows.length ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No reviewers yet — new requests will wait unassigned until you add layer 1.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Layer</th>
              <th>Added by</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.email}</td>
                <td className="muted">{r.displayName ?? '—'}</td>
                <td>
                  <select
                    className="class-select class-select--sm"
                    value={r.layer}
                    disabled={busy}
                    onChange={(e) => void update({ variables: { id: Number(r.id), layer: Number(e.target.value) } })}
                  >
                    {[1, 2, 3].map((l) => (
                      <option key={l} value={l}>
                        L{l}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="muted">{r.createdBy}</td>
                <td>
                  <button
                    className="btn btn-sm btn-danger"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(`Remove ${r.email} from the reviewer list? Open requests keep them assigned.`))
                        void remove({ variables: { id: Number(r.id) } });
                    }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
